import { ReactNode } from "react";
import { Avatar, Button, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import LabelImportantIcon from '@mui/icons-material/LabelImportant';

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import AdminReportsTable, { TReportsTableShowData } from "~/tables/AdminReportsTable";
import { useTrans } from '~/hooks/useTrans';
import { apiGetReports } from "~/providers/api";
import { IReportItem } from "~/providers/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import MyAlert from "~/mui/MyAlert";
import { getReportItems } from "~/utils/utils";
import { DEFAULT_BUSSINESS_PLAN_TYPE_FILTER } from "~/const";
import BussinessPlanSelect from "~/components/BussinessPlanSelect";
import { projectsFilterStore } from "~/stores/projectsFilter";
import BillingMethodSelect from "~/components/BillingMethodSelect";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	reports: Array<IReportItem>;
	errorMsg: string;
	popupData: ReactNode;
	businessPlanType: string;
	billingMethod: string;
}

const ENABLE = true;

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	reports: [],
	errorMsg: "",
	popupData: null,
	businessPlanType: DEFAULT_BUSSINESS_PLAN_TYPE_FILTER,
	billingMethod: null,
	...ENABLE
		? {}
		: {
			loading: false,
			errorMsg: "Výpis reportů je dočasně pozastavený",
		},
};

export default function AdminReportsPage() {
	const { trans } = useTrans();
	const { lang } = useLang();

	function getInitState(): IState {
		const filtersState = projectsFilterStore.getState();

		return {
			...INIT_STATE,
			...filtersState.data.filter.businessPlanType
				? {
					businessPlanType: filtersState.data.filter.businessPlanType,
				}
				: {},
		};
	}

	const { state, updateState, setState } = myUseState<IState>(getInitState());

	async function loadData() {
		updateState({
			loading: true,
			reports: [],
			errorMsg: "",
		});

		const reportsData = await apiGetReports(state.businessPlanType, state.billingMethod, lang);

		updateState({
			loading: false,
			...reportsData.noErrors
				? {
					reports: reportsData.data,
				}
				: {},
			errorMsg: reportsData.errors.join(", "),
		});
	}

	function onShowData(type: TReportsTableShowData, report: IReportItem) {
		/* eslint-disable react/no-array-index-key */
		const items = getReportItems(type, report);
		const popupData = <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
			{ items.map((item, ind) => <ListItem key={ind}>
				<ListItemAvatar>
					<Avatar>
						<LabelImportantIcon />
					</Avatar>
				</ListItemAvatar>
				<ListItemText primary={item.title} secondary={item.subTitle} primaryTypographyProps={{ fontWeight: "bold" }} secondaryTypographyProps={{ color: "#000" }} />
			</ListItem>) }
		</List>;

		updateState({
			popupData,
		});
	}

	function updateFilterBusinessPlan(value: string | null) {
		const curGlobFilter = projectsFilterStore.getState().data.filter;
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("businessPlanType" in curFilter) {
				delete curFilter.businessPlanType;
			}

			if ("businessPlanType" in curGlobFilter) {
				delete curGlobFilter.businessPlanType;
			}
		} else {
			curFilter.businessPlanType = value;
			curGlobFilter.businessPlanType = value;
		}

		setState(curFilter);
		projectsFilterStore.getState().setFilter(curGlobFilter);
	}

	function updateBillingMethod(value: string | null) {
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("billingMethod" in curFilter) {
				delete curFilter.billingMethod;
			}
		} else {
			curFilter.billingMethod = value;
		}

		setState(curFilter);
	}

	function sendFilter() {
		loadData();
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized && ENABLE) {
			setState(getInitState());
			loadData();
		}
	}

	return <Page title={trans("menu.adminReports2")} breadcrumbs={[trans("administration"), trans("menu.adminReports2")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.transactions">
		<TableControls className="adminServicesPage__tableControls">
			<div className="adminServicesPage__tableControlsLine">
				<BussinessPlanSelect value={state.businessPlanType} onChange={updateFilterBusinessPlan} />
				<BillingMethodSelect value={state.billingMethod} onChange={updateBillingMethod} />
				<Button color="success" variant="contained" onClick={sendFilter}>
					{ trans("filter") }
				</Button>
			</div>
		</TableControls>
		<AdminReportsTable loading={state.loading} items={state.reports} showData={onShowData} />
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.popupData && <MyAlert onClose={() => updateState({ popupData: null })} title="Seznam položek" cancelCaption={trans("close")}>
			{ state.popupData }
		</MyAlert>}
	</Page>;
}
